import { useMemo, useState, useEffect, useCallback } from "react";
import { IItemProps } from "devextreme-react/form";

import { ILogger } from "@ezhou/common";
import { PageProps } from "@ezhou/core";

import { SaveChangeResult } from "../../components/dataGridActionUtil";
import { OrganizationUser, User, UserBase } from "../../model/apiModel/systemadminmodel";

import { CrudDataGrid } from "../common/CrudDataGrid";

import { OrganizationModel } from "./model/model";
import { dummyOrganizationUserRow } from "./model/metaData";

export type OrganizationUsersProps = {
  masterData: OrganizationModel;
  pageProps: PageProps;
  logger: ILogger;
};

export const OrganizationUsers = (props: OrganizationUsersProps) => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUser[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserBase[]>([]);

  const excludedColumns = useMemo(() => new Set<string>([
    "organizationId",
  ]), []);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await props.pageProps.appUtil.httpClients["eAccountingService"].get<User[]>(
          `/users/get-all`,
          (err) => {
            props.logger.error("Failed to fetch available users", err);
          },
        );
        if (response) {
          setAllUsers(response.filter(u => u.userType == "OrganizationUser"));
        }
      } catch (error) {
        props.logger.error("Error fetching available users", error);
      }
    };
    fetchAllUsers();
  }, [props.masterData.organizationId, props.pageProps.appUtil.httpClients, props.logger]);

  useEffect(() => {
    // Calculate available users by excluding organization users from all users
    const orgUserIds = new Set(organizationUsers.map(ou => ou.userId));
    const available = allUsers.filter(user => !orgUserIds.has(user.userId));
    setAvailableUsers(available);
  }, [allUsers, organizationUsers]);

  const refreshData = useCallback(async () => {
    const response = await props.pageProps.appUtil.httpClients["eAccountingService"].get<OrganizationUser[]>(
      `/organizations/get-users/${props.masterData.organizationId}`,
      (err) => {
        throw err;
      },
    );
    if (response) {
      setOrganizationUsers(response);
      return response;
    }
    var ret : OrganizationUser[] = [];
    setOrganizationUsers(ret);
    return ret;
  }, [props.masterData.organizationId, props.pageProps.appUtil.httpClients]);

  const filterRowDataForForm = (data: any) => {
    return { userId: data.userId };
  };

  const editorItemOptions = useMemo(() => new Map<string, IItemProps>([
    [
      "userId",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          dataSource: availableUsers,
          displayExpr: "userName",
          valueExpr: "userId",
          searchEnabled: true,
          placeholder: "Select a user",
        },
      },
    ],
  ]), [availableUsers]);

  const sendChange = async (change: {
    type: any;
    data: any;
    key: any;
  }): Promise<SaveChangeResult> => {
    switch (change.type) {
      case "insert":
        const response = await props.pageProps.appUtil.httpClients["eAccountingService"].post(
          `organizations/add-user`,
          { organizationId: props.masterData.organizationId, ...change.data },
          (err: any) => {
            throw err;
          },
        );
        if (response) {
          return {
            data: { 
              organizationId: props.masterData.organizationId,
              userName: allUsers.find(u => u.userId === change.data.userId)?.userName ?? "Unknown",
              ...change.data
            },
            keyExpr: "userId",
          };
        }
        break;
      case "update":
        break;
      case "remove":
        const responseRemove = await props.pageProps.appUtil.httpClients["eAccountingService"].post(
          `organizations/remove-user`,
          { organizationId: props.masterData.organizationId, userId: change.key },
          (err: any) => {
            throw err;
          },
        );
        break;
      default:
        break;
    }
    return {
      data: change.data,
      keyExpr: "userId",
    };
  };

  return (
    <>
      <div>Users</div>
      <CrudDataGrid
        pageProps={props.pageProps}
        logger={props.logger}
        dummyRow={dummyOrganizationUserRow}
        keyExpr={"userId"}
        formTitle="Add User"
        formColCount={2}
        formColSpan={2}
        refreshData={refreshData}
        allowAdding={true}
        allowUpdating={false}
        allowDeleting={true}
        filterRowDataForForm={filterRowDataForForm}
        editorItemOptions={editorItemOptions}
        sendChange={sendChange}
        hideColumnChooser={true}
        hideSearchPanel={true}
        excludedColumns={excludedColumns}
      ></CrudDataGrid>
    </>
  );
};
